import { useState, useEffect, useCallback, useRef, memo, useMemo } from "react";
import { Polygon, Marker, Tooltip, useMapEvents, useMap, Circle } from "react-leaflet";
import L from "leaflet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as MiniCalendar } from "@/components/ui/calendar";
import { da } from "date-fns/locale";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { Save, X, Trash2, MapPin, Clock, Move, CalendarDays } from "lucide-react";

// Lag-rækkefølge på kortet (højere z-index = øverst og klikbar først):
//   rengøringszoner (lag 1, øverst) > arbejdszoner (lag 2) > områder (lag 3, nederst)
const ZONE_PANES = {
  area: { name: 'zm-area-pane', zIndex: 410 },
  workZone: { name: 'zm-workzone-pane', zIndex: 420 },
  cleaningZone: { name: 'zm-cleaningzone-pane', zIndex: 430 },
};

// Komponent der opretter de tre custom panes på det Leaflet-kort vi sidder i
function PaneInitializer() {
  const map = useMap();
  useEffect(() => {
    Object.values(ZONE_PANES).forEach(({ name, zIndex }) => {
      if (!map.getPane(name)) {
        const p = map.createPane(name);
        p.style.zIndex = String(zIndex);
      }
    });
  }, [map]);
  return null;
}

// Zone typer
const ZONE_TYPES = {
  area: { label: "Område", color: "#6366f1", description: "Store festivalområder" },
  workZone: { label: "Arbejdszone", color: "#22c55e", description: "Frivilliges arbejdsområde" },
  cleaningZone: { label: "Rengøringszone", color: "#ef4444", description: "Små rengøringsområder" }
};

// Status farver for rengøringszoner
const CLEANING_STATUS = {
  clean: { color: "#666666", label: "Rent", opacity: 0 },
  pending: { color: "#ef4444", label: "Skal rengøres", opacity: 0.5 },
  in_progress: { color: "#eab308", label: "På vej", opacity: 0.5 }
};

// Beregn polygon centrum
function getPolygonCenter(corners) {
  if (!corners || corners.length === 0) return null;
  const lat = corners.reduce((sum, c) => sum + c.lat, 0) / corners.length;
  const lng = corners.reduce((sum, c) => sum + c.lng, 0) / corners.length;
  return { lat, lng };
}

// Beregn punkt på linje (midtpunkt af øverste kant)
function getTopEdgeMidpoint(corners) {
  if (!corners || corners.length < 2) return null;
  // Find de to nordligste punkter (højeste lat)
  const sorted = [...corners].sort((a, b) => b.lat - a.lat);
  const top1 = sorted[0];
  const top2 = sorted[1];
  return {
    lat: (top1.lat + top2.lat) / 2,
    lng: (top1.lng + top2.lng) / 2
  };
}

// Polygon komponent med interaktiv styling — memoized for at undgå unødige re-renders ved zoom/pan
const ZonePolygon = memo(function ZonePolygon({ zone, type, isSelected, onSelect, userRole, isDrawingMode }) {
  const config = ZONE_TYPES[type];
  let fillColor = config.color;
  let fillOpacity = 0;
  let strokeColor = config.color;
  let strokeWeight = 2;
  let dashArray = null;
  const isAdminOrHeartLeader = userRole === 'admin' || userRole === 'heart_leader';
  let shouldHide = false;

  // Rengøringszoner styling
  if (type === 'cleaningZone') {
    if (zone.status === 'clean') {
      if (isAdminOrHeartLeader) {
        strokeColor = '#888888';
        dashArray = '6, 6';
        fillOpacity = 0;
      } else {
        shouldHide = true;
      }
    } else {
      const statusConfig = CLEANING_STATUS[zone.status] || CLEANING_STATUS.pending;
      fillColor = statusConfig.color;
      fillOpacity = statusConfig.opacity;
      strokeColor = statusConfig.color;
    }
  }

  // Områder og arbejdszoner: kun streg
  if (type === 'area' || type === 'workZone') {
    fillOpacity = 0;
    strokeWeight = 3;
  }

  if (isSelected) {
    strokeColor = '#3b82f6';
    strokeWeight = 4;
    dashArray = null;
  }

  if (isSelected) {
    strokeColor = '#3b82f6';
    strokeWeight = 4;
    dashArray = null;
  }

  // Label position - på øverste kant for arbejdszoner
  const labelPosition = type === 'workZone' ? getTopEdgeMidpoint(zone.corners) : null;

  // Alle zonetyper er klikbare når ikke i tegningsmodus
  const isClickable = !isDrawingMode;

  // Stabile positions + pathOptions så Leaflet ikke unødigt redraw'er ved hver render
  const positions = useMemo(
    () => zone.corners?.map(c => [c.lat, c.lng]) || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [zone.corners, zone.updated_at, zone.id]
  );
  const pathOptions = useMemo(() => ({
    color: strokeColor,
    fillColor,
    fillOpacity,
    weight: strokeWeight,
    dashArray,
    interactive: isClickable,
    pane: ZONE_PANES[type]?.name,
    className: !isClickable ? 'zone-non-interactive' : ''
  }), [strokeColor, fillColor, fillOpacity, strokeWeight, dashArray, isClickable, type]);

  if (shouldHide) return null;

  return (
    <>
      <Polygon
        positions={positions}
        pathOptions={pathOptions}
        eventHandlers={isClickable ? {
          click: (e) => {
            L.DomEvent.stopPropagation(e);
            onSelect?.(zone, type);
          }
        } : {}}
      >
        {type === 'workZone' && labelPosition && (
          <Tooltip 
            permanent 
            direction="top" 
            offset={[0, -5]}
            className="zone-edge-label"
          >
            <span style={{ 
              fontWeight: 'bold', 
              fontSize: '13px',
              color: '#22c55e',
              textShadow: '1px 1px 2px black, -1px -1px 2px black, 1px -1px 2px black, -1px 1px 2px black',
              background: 'transparent',
              padding: '0'
            }}>
              {zone.name || zone.label}
            </span>
          </Tooltip>
        )}
      </Polygon>
    </>
  );
}, (prev, next) => {
  // Custom comparator: re-render kun hvis zonens data faktisk er ændret
  return (
    prev.zone === next.zone &&
    prev.zone?.updated_at === next.zone?.updated_at &&
    prev.zone?.status === next.zone?.status &&
    prev.zone?.name === next.zone?.name &&
    prev.type === next.type &&
    prev.isSelected === next.isSelected &&
    prev.userRole === next.userRole &&
    prev.isDrawingMode === next.isDrawingMode &&
    prev.onSelect === next.onSelect
  );
});

// Interaktiv hjørne-editor med native Leaflet - FORBEDRET VERSION
function CornerEditor({ corners, onChange, color, map }) {
  const markersRef = useRef([]);
  const polygonRef = useRef(null);
  const cornersRef = useRef(corners); // Hold styr på corners uden at trigge re-render
  const isInitializedRef = useRef(false);

  // Opdater cornersRef når corners ændres udefra
  useEffect(() => {
    cornersRef.current = corners;
  }, [corners]);

  // Opret markører og polygon KUN én gang
  useEffect(() => {
    if (!map || !corners || corners.length === 0 || isInitializedRef.current) return;
    
    isInitializedRef.current = true;

    // Opret polygon preview
    polygonRef.current = L.polygon(
      corners.map(c => [c.lat, c.lng]),
      { 
        color: color, 
        fillColor: color, 
        fillOpacity: 0.2,
        weight: 3,
        dashArray: '5, 5'
      }
    ).addTo(map);

    // Opret trækbare markører for hvert hjørne
    corners.forEach((corner, index) => {
      const marker = L.marker([corner.lat, corner.lng], {
        draggable: true,
        autoPan: true,
        icon: L.divIcon({
          html: `<div style="
            width: 24px; 
            height: 24px; 
            background: ${color}; 
            border: 3px solid white; 
            border-radius: 4px;
            cursor: grab;
            box-shadow: 0 3px 8px rgba(0,0,0,0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 11px;
            font-weight: bold;
            color: white;
          ">${index + 1}</div>`,
          className: 'corner-marker-native',
          iconSize: [24, 24],
          iconAnchor: [12, 12]
        })
      }).addTo(map);

      // Drag event - opdater polygon preview i REALTID
      marker.on('drag', (e) => {
        const { lat, lng } = e.target.getLatLng();
        
        // Opdater vores lokale reference
        const newCorners = [...cornersRef.current];
        newCorners[index] = { lat, lng };
        cornersRef.current = newCorners;
        
        // Opdater polygon preview visuelt
        if (polygonRef.current) {
          polygonRef.current.setLatLngs(newCorners.map(c => [c.lat, c.lng]));
        }
      });

      // Dragend - gem til state (men vi kalder IKKE onChange under drag for at undgå re-render)
      marker.on('dragend', (e) => {
        // Vent lidt for at undgå race conditions
        setTimeout(() => {
          onChange([...cornersRef.current]);
        }, 50);
      });

      markersRef.current.push(marker);
    });

    // Cleanup funktion
    return () => {
      markersRef.current.forEach(m => {
        m.off('drag');
        m.off('dragend');
        m.remove();
      });
      markersRef.current = [];
      if (polygonRef.current) {
        polygonRef.current.remove();
        polygonRef.current = null;
      }
      isInitializedRef.current = false;
    };
  }, [map, color]); // IKKE corners som dependency!

  return null;
}

// Rektangel-tegneværktøj
function RectangleDrawer({ zoneType, onComplete, onCancel }) {
  const [startPoint, setStartPoint] = useState(null);
  const [previewCorners, setPreviewCorners] = useState([]);
  const map = useMap();
  const config = ZONE_TYPES[zoneType];

  useMapEvents({
    click: (e) => {
      if (!startPoint) {
        setStartPoint({ lat: e.latlng.lat, lng: e.latlng.lng });
        toast.info("Klik på det modsatte hjørne");
      } else {
        const endPoint = { lat: e.latlng.lat, lng: e.latlng.lng };
        // 8 punkter: 4 hjørner + 4 midtpunkter på kanterne
        const tl = { lat: startPoint.lat, lng: startPoint.lng };
        const tr = { lat: startPoint.lat, lng: endPoint.lng };
        const br = { lat: endPoint.lat, lng: endPoint.lng };
        const bl = { lat: endPoint.lat, lng: startPoint.lng };
        const corners = [
          tl,
          { lat: (tl.lat + tr.lat) / 2, lng: (tl.lng + tr.lng) / 2 },
          tr,
          { lat: (tr.lat + br.lat) / 2, lng: (tr.lng + br.lng) / 2 },
          br,
          { lat: (br.lat + bl.lat) / 2, lng: (br.lng + bl.lng) / 2 },
          bl,
          { lat: (bl.lat + tl.lat) / 2, lng: (bl.lng + tl.lng) / 2 },
        ];
        onComplete(corners);
      }
    },
    mousemove: (e) => {
      if (startPoint) {
        const endPoint = { lat: e.latlng.lat, lng: e.latlng.lng };
        const tl = { lat: startPoint.lat, lng: startPoint.lng };
        const tr = { lat: startPoint.lat, lng: endPoint.lng };
        const br = { lat: endPoint.lat, lng: endPoint.lng };
        const bl = { lat: endPoint.lat, lng: startPoint.lng };
        setPreviewCorners([
          tl,
          { lat: (tl.lat + tr.lat) / 2, lng: (tl.lng + tr.lng) / 2 },
          tr,
          { lat: (tr.lat + br.lat) / 2, lng: (tr.lng + br.lng) / 2 },
          br,
          { lat: (br.lat + bl.lat) / 2, lng: (br.lng + bl.lng) / 2 },
          bl,
          { lat: (bl.lat + tl.lat) / 2, lng: (bl.lng + tl.lng) / 2 },
        ]);
      }
    }
  });

  return (
    <>
      {previewCorners.length >= 4 && (
        <Polygon
          positions={previewCorners.map(c => [c.lat, c.lng])}
          pathOptions={{
            color: config.color,
            fillColor: config.color,
            fillOpacity: 0.3,
            weight: 2,
            dashArray: '8, 8'
          }}
        />
      )}

      {startPoint && (
        <Circle
          center={[startPoint.lat, startPoint.lng]}
          radius={5}
          pathOptions={{ color: config.color, fillColor: config.color, fillOpacity: 1 }}
        />
      )}

      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] bg-black/90 border border-white/20 rounded-lg shadow-lg p-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: config.color }} />
            <span className="text-sm font-medium text-white">Tegner {config.label}</span>
          </div>
          <div className="text-xs text-gray-400">
            {!startPoint ? "Klik for at starte" : "Klik for at afslutte"}
          </div>
          <Button size="sm" variant="outline" onClick={onCancel} className="text-white border-white/30">
            <X className="h-4 w-4 mr-1" />
            Annuller
          </Button>
        </div>
      </div>
    </>
  );
}

// Komponent til at administrere planlagte tidspunkter for eksisterende zoner
function ScheduledTimesManager({ zone, token, onUpdate }) {
  const [newTime, setNewTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [localTimes, setLocalTimes] = useState(zone.scheduled_times || []);
  
  // Opdater lokale tider når zone ændres udefra
  useEffect(() => {
    setLocalTimes(zone.scheduled_times || []);
  }, [zone.scheduled_times]);
  
  const addTime = async () => {
    if (!newTime) {
      toast.error("Vælg et tidspunkt");
      return;
    }
    
    setLoading(true);
    try {
      const isoTime = new Date(newTime).toISOString();
      await axios.post(
        `${API}/cleaning-zones/${zone.id}/schedule?time=${encodeURIComponent(isoTime)}`, 
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Opdater lokalt med det samme
      setLocalTimes(prev => [...prev, isoTime].sort());
      toast.success("Tidspunkt tilføjet");
      setNewTime("");
      onUpdate(); // Opdater også parent
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved tilføjelse");
    } finally {
      setLoading(false);
    }
  };
  
  const removeTime = async (timeToRemove) => {
    setLoading(true);
    try {
      await axios.delete(
        `${API}/cleaning-zones/${zone.id}/schedule?time=${encodeURIComponent(timeToRemove)}`, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Opdater lokalt med det samme
      setLocalTimes(prev => prev.filter(t => t !== timeToRemove));
      toast.success("Tidspunkt fjernet");
      onUpdate(); // Opdater også parent
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved fjernelse");
    } finally {
      setLoading(false);
    }
  };
  
  // Formater dato til kort dansk format (dd/mm kl. HH:MM)
  const formatDateTime = (isoString) => {
    try {
      const date = new Date(isoString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day}/${month} kl. ${hours}:${minutes}`;
    } catch {
      return isoString;
    }
  };
  
  return (
    <div className="mt-4 pt-3 border-t border-white/10">
      <Label className="text-xs uppercase tracking-wider text-gray-400 flex items-center gap-1 mb-2">
        <Clock className="h-3 w-3" />
        Planlagte aktiveringer
      </Label>
      
      {/* Liste over eksisterende tider */}
      {localTimes.length > 0 ? (
        <div className="space-y-1 mb-2 max-h-48 overflow-y-auto overscroll-contain pr-1">
          {localTimes.map((time, idx) => (
            <div key={idx} className="flex items-center justify-between bg-white/5 rounded px-2 py-1">
              <span className="text-sm text-white">
                {formatDateTime(time)}
              </span>
              <button 
                onClick={() => removeTime(time)}
                disabled={loading}
                className="text-red-400 hover:text-red-300 text-xs disabled:opacity-50"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-500 mb-2">Ingen planlagte aktiveringer</p>
      )}
      
      {/* Tilføj ny tid - custom input med kortere format + mini-kalender */}
      <div className="space-y-2">
        <div className="flex gap-2">
          <Input
            type="datetime-local"
            value={newTime}
            onChange={(e) => setNewTime(e.target.value)}
            min="2024-01-01T00:00"
            max="2099-12-31T23:59"
            className="flex-1 h-8 text-xs bg-white/10 border-white/20 text-white [&::-webkit-datetime-edit-year-field]:w-8"
            disabled={loading}
          />
          <Popover>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="text-white border-white/30 h-8 shrink-0 px-2"
                disabled={loading}
                aria-label="Vælg dag fra mini-kalender"
                data-testid="cleaning-zone-date-picker-btn"
              >
                <CalendarDays className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <MiniCalendar
                mode="single"
                locale={da}
                weekStartsOn={1}
                selected={newTime && newTime.includes('T') ? new Date(newTime) : undefined}
                onSelect={(d) => {
                  if (!d) return;
                  const y = d.getFullYear();
                  const m = String(d.getMonth() + 1).padStart(2, '0');
                  const day = String(d.getDate()).padStart(2, '0');
                  // Behold eksisterende klokkeslæt, ellers default 06:00
                  const timePart = (newTime && newTime.includes('T')) ? newTime.split('T')[1] : '06:00';
                  setNewTime(`${y}-${m}-${day}T${timePart}`);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <Button 
            onClick={addTime} 
            size="sm" 
            variant="outline" 
            className="text-white border-white/30 h-8"
            disabled={loading || !newTime}
          >
            +
          </Button>
        </div>
        <p className="text-xs text-gray-500">Vælg dag via kalender-ikonet — eller tast dd-mm-åå tt:mm</p>
      </div>
    </div>
  );
}

// Hoved Zone Manager
export default function ZoneManager({ 
  eventId, 
  userRole, 
  userSelectedRole,  // Den valgte rolle ved frivillig-login (f.eks. "Sweep 1")
  onZoneSelect,
  showAreas = true,
  showWorkZones = true,
  showCleaningZones = true,
  drawCommand = null,
  onDrawComplete = null,
}) {
  const map = useMap();
  // Callback-ref: stop scroll/klik fra at zoome/panne kortet når man interagerer med info-panelet
  const stopMapInteraction = useCallback((el) => {
    if (el) {
      L.DomEvent.disableScrollPropagation(el);
      L.DomEvent.disableClickPropagation(el);
    }
  }, []);
  const [areas, setAreas] = useState([]);
  const [workZones, setWorkZones] = useState([]);
  const [cleaningZones, setCleaningZones] = useState([]);
  
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingType, setDrawingType] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [pendingCorners, setPendingCorners] = useState(null);
  const [isEditingCorners, setIsEditingCorners] = useState(false);
  
  const [newZoneName, setNewZoneName] = useState("");
  const [selectedAreaId, setSelectedAreaId] = useState("");
  const [scheduledTimes, setScheduledTimes] = useState([]);
  const [newScheduledTime, setNewScheduledTime] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [editingName, setEditingName] = useState(false);
  const [tempName, setTempName] = useState('');
  const [editingDescription, setEditingDescription] = useState(false);
  const [tempDescription, setTempDescription] = useState('');
  const [teams, setTeams] = useState([]);  // event_roles — bruges til read-only "tildelte hold"-oversigt
  const [roleFilteredAreaId, setRoleFilteredAreaId] = useState(null);  // Område filtreret baseret på rolle
  
  const token = localStorage.getItem("token");
  const isAdmin = userRole === 'admin';
  const canCreateCleaningZone = userRole === 'admin' || userRole === 'heart_leader';

  // Håndter tegne-kommando fra toolbar
  useEffect(() => {
    if (drawCommand) {
      setDrawingType(drawCommand);
      setIsDrawing(true);
      setSelectedZone(null);
      setSelectedType(null);
    }
  }, [drawCommand]);

  useEffect(() => {
    if (eventId) loadAllZones();
  }, [eventId, userSelectedRole]);

  // Auto-opdater rengøringszoner hver 2 minutter for at fange planlagte aktiveringer
  // + lyt på 'resovia-reload-zones' event fra flydende reload-knap / Lag-menu
  useEffect(() => {
    if (!eventId) return;
    
    const refreshCleaningZones = async () => {
      try {
        if (userSelectedRole && userRole !== 'admin' && userRole !== 'heart_leader') {
          const roleZonesRes = await axios.get(
            `${API}/cleaning-zones/for-role?event_id=${eventId}&role_name=${encodeURIComponent(userSelectedRole)}`
          );
          setCleaningZones(roleZonesRes.data.zones || []);
        } else {
          const cleaningZonesRes = await axios.get(`${API}/cleaning-zones?event_id=${eventId}`);
          setCleaningZones(cleaningZonesRes.data);
        }
      } catch (error) {
        console.error("Auto-opdatering af rengøringszoner fejlede", error);
      }
    };
    
    // Poll hver 2 minutter (tidligere 30 sekunder — unødigt og belastede kortet)
    // Polling er kun for at fange ændringer lavet af andre admins/enheder.
    const interval = setInterval(refreshCleaningZones, 2 * 60 * 1000);
    // Manuel trigger fra reload-knap
    const manualReload = () => refreshCleaningZones();
    window.addEventListener('resovia-reload-zones', manualReload);
    return () => {
      clearInterval(interval);
      window.removeEventListener('resovia-reload-zones', manualReload);
    };
  }, [eventId, userSelectedRole, userRole]);

  const loadAllZones = async () => {
    try {
      // Hent områder og arbejdszoner normalt
      const [areasRes, workZonesRes] = await Promise.all([
        axios.get(`${API}/areas?event_id=${eventId}`),
        axios.get(`${API}/work-zones?event_id=${eventId}`)
      ]);
      setAreas(areasRes.data);
      setWorkZones(workZonesRes.data);
      
      // Hent TEAMS (event_roles) — bruges til read-only oversigt over tildelte hold pr. zone
      if (isAdmin || canCreateCleaningZone) {
        try {
          const teamsRes = await axios.get(`${API}/events/${eventId}/roles`);
          setTeams(teamsRes.data || []);
        } catch (e) {
          // Lyt ikke i konsollen ved fejl — bare hav en tom liste
        }
      }
      
      // Hvis brugeren har en valgt rolle (frivillig), hent zoner for den rolle
      if (userSelectedRole && userRole !== 'admin' && userRole !== 'heart_leader') {
        const roleZonesRes = await axios.get(
          `${API}/cleaning-zones/for-role?event_id=${eventId}&role_name=${encodeURIComponent(userSelectedRole)}`
        );
        setCleaningZones(roleZonesRes.data.zones || []);
        setRoleFilteredAreaId(roleZonesRes.data.assigned_area_id);
      } else {
        // Admin/Heart Leader ser alle zoner
        const cleaningZonesRes = await axios.get(`${API}/cleaning-zones?event_id=${eventId}`);
        setCleaningZones(cleaningZonesRes.data);
        setRoleFilteredAreaId(null);
      }
    } catch (error) {
      console.error("Kunne ikke hente zoner", error);
    }
  };

  const handleDrawComplete = (corners) => {
    setPendingCorners(corners);
    setIsDrawing(false);
    setIsEditingCorners(true); // Start med at redigere hjørner med det samme!
    setShowCreateDialog(true);
    
    // Auto-navngiv
    if (drawingType === 'area') {
      setNewZoneName(`Område ${areas.length + 1}`);
    } else if (drawingType === 'workZone') {
      setNewZoneName(`Arbejdszone ${workZones.length + 1}`);
    } else {
      setNewZoneName(`Rengøringszone ${cleaningZones.length + 1}`);
    }
  };

  const handleCornersChange = (newCorners) => {
    setPendingCorners(newCorners);
  };

  const createZone = async () => {
    if (!newZoneName.trim()) {
      toast.error("Indtast et navn");
      return;
    }
    if (!pendingCorners || pendingCorners.length < 3) {
      toast.error("Mangler hjørner");
      return;
    }

    try {
      let endpoint = '';
      let payload = {};

      if (drawingType === 'area') {
        endpoint = `${API}/areas`;
        payload = { name: newZoneName, corners: pendingCorners, event_id: eventId };
      } else if (drawingType === 'workZone') {
        if (!selectedAreaId) {
          toast.error("Vælg et område");
          return;
        }
        endpoint = `${API}/work-zones`;
        payload = { name: newZoneName, corners: pendingCorners, area_id: selectedAreaId, event_id: eventId };
      } else if (drawingType === 'cleaningZone') {
        if (!selectedAreaId) {
          toast.error("Vælg et område");
          return;
        }
        endpoint = `${API}/cleaning-zones`;
        payload = { 
          name: newZoneName, 
          corners: pendingCorners, 
          area_id: selectedAreaId, 
          event_id: eventId,
          scheduled_times: scheduledTimes.map(t => new Date(t).toISOString())
        };
      }

      await axios.post(endpoint, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${ZONE_TYPES[drawingType].label} oprettet!`);
      resetDialog();
      loadAllZones();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved oprettelse");
    }
  };

  const addScheduledTime = () => {
    if (!newScheduledTime) {
      toast.error("Vælg et tidspunkt");
      return;
    }
    // Konverter lokal tid til ISO UTC format
    const localDate = new Date(newScheduledTime);
    const isoTime = localDate.toISOString();
    
    if (scheduledTimes.includes(isoTime)) {
      toast.error("Tidspunkt er allerede tilføjet");
      return;
    }
    setScheduledTimes([...scheduledTimes, isoTime].sort());
    setNewScheduledTime("");
  };

  const removeScheduledTime = (timeToRemove) => {
    setScheduledTimes(scheduledTimes.filter(t => t !== timeToRemove));
  };

  const updateZoneCorners = async () => {
    if (!selectedZone || !pendingCorners) return;

    try {
      let endpoint = '';
      if (selectedType === 'area') endpoint = `${API}/areas/${selectedZone.id}`;
      else if (selectedType === 'workZone') endpoint = `${API}/work-zones/${selectedZone.id}`;
      else endpoint = `${API}/cleaning-zones/${selectedZone.id}`;

      await axios.put(endpoint, { corners: pendingCorners }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Zone opdateret!");
      setIsEditingCorners(false);
      setPendingCorners(null);
      loadAllZones();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved opdatering");
    }
  };

  const renameZone = async () => {
    if (!selectedZone || !tempName.trim()) return;
    try {
      let endpoint = '';
      if (selectedType === 'area') endpoint = `${API}/areas/${selectedZone.id}`;
      else if (selectedType === 'workZone') endpoint = `${API}/work-zones/${selectedZone.id}`;
      else endpoint = `${API}/cleaning-zones/${selectedZone.id}`;

      await axios.put(endpoint, { name: tempName.trim(), label: tempName.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Zone omdøbt!");
      setEditingName(false);
      loadAllZones();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved omdøbning");
    }
  };

  // Skift hvilket overordnet område en arbejdszone/rengøringszone hører til
  const changeZoneArea = async (newAreaId) => {
    if (!selectedZone || (selectedType !== 'workZone' && selectedType !== 'cleaningZone')) return;
    try {
      const endpoint = selectedType === 'workZone'
        ? `${API}/work-zones/${selectedZone.id}`
        : `${API}/cleaning-zones/${selectedZone.id}`;
      await axios.put(endpoint, { area_id: newAreaId || null }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Område opdateret!");
      // Opdater den lokale selectedZone så UI'et viser ændringen med det samme
      setSelectedZone({ ...selectedZone, area_id: newAreaId || null });
      loadAllZones();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved opdatering af område");
    }
  };

  // Helper: byg endpoint baseret på selectedType
  const endpointForSelected = () => {
    if (!selectedZone) return null;
    if (selectedType === 'area') return `${API}/areas/${selectedZone.id}`;
    if (selectedType === 'workZone') return `${API}/work-zones/${selectedZone.id}`;
    if (selectedType === 'cleaningZone') return `${API}/cleaning-zones/${selectedZone.id}`;
    return null;
  };

  // Gem ny beskrivelse (virker for områder, arbejdszoner og rengøringszoner)
  const saveDescription = async () => {
    const endpoint = endpointForSelected();
    if (!endpoint) return;
    try {
      await axios.put(
        endpoint,
        { description: tempDescription },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Beskrivelse opdateret");
      setSelectedZone({ ...selectedZone, description: tempDescription });
      setEditingDescription(false);
      loadAllZones();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved opdatering af beskrivelse");
    }
  };

  // Tildelte hold er nu read-only og udledes via assignedTeams (se nedenfor)
  // — redigering sker i Stamdata > Teams. toggleTeam er fjernet med vilje.

  const showConfirm = (title, message, onConfirm) => {
    setConfirmDialog({ open: true, title, message, onConfirm });
  };

  const closeConfirm = () => {
    setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
  };

  const deleteZone = () => {
    if (!selectedZone) return;
    showConfirm(
      "Slet zone?",
      `Er du sikker på du vil slette "${selectedZone.name}"? Denne handling kan ikke fortrydes.`,
      async () => {
        closeConfirm();
        try {
          let endpoint = '';
          if (selectedType === 'area') endpoint = `${API}/areas/${selectedZone.id}`;
          else if (selectedType === 'workZone') endpoint = `${API}/work-zones/${selectedZone.id}`;
          else endpoint = `${API}/cleaning-zones/${selectedZone.id}`;

          await axios.delete(endpoint, { headers: { Authorization: `Bearer ${token}` } });
          toast.success("Zone slettet");
          setSelectedZone(null);
          setSelectedType(null);
          loadAllZones();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Fejl ved sletning");
        }
      }
    );
  };

  const handleCleaningAction = async (action) => {
    if (!selectedZone || selectedType !== 'cleaningZone') return;
    try {
      await axios.post(`${API}/cleaning-zones/${selectedZone.id}/${action}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const messages = { 'request-cleaning': 'Rengøring bestilt!', 'on-my-way': 'På vej!', 'complete': 'Rengøring færdig!' };
      toast.success(messages[action]);
      loadAllZones();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl");
    }
  };

  const resetDialog = () => {
    setShowCreateDialog(false);
    setPendingCorners(null);
    setIsEditingCorners(false);
    setNewZoneName("");
    setSelectedAreaId("");
    setScheduledTimes([]);
    setNewScheduledTime("");
    setDrawingType(null);
    if (onDrawComplete) onDrawComplete();
  };

  const handleZoneSelect = useCallback((zone, type) => {
    setSelectedZone(zone);
    setSelectedType(type);
    setIsEditingCorners(false);
    setPendingCorners(null);
    setEditingName(false);
    setEditingDescription(false);
    if (onZoneSelect) onZoneSelect(zone, type);
  }, [onZoneSelect]);

  // Tilføj midtpunkter mellem eksisterende hjørner (opgradér 4-punkts zoner til 8)
  const addMidpointsIfNeeded = (corners) => {
    if (!corners || corners.length !== 4) return corners;
    const result = [];
    for (let i = 0; i < corners.length; i++) {
      const curr = corners[i];
      const next = corners[(i + 1) % corners.length];
      result.push(curr);
      result.push({ lat: (curr.lat + next.lat) / 2, lng: (curr.lng + next.lng) / 2 });
    }
    return result;
  };

  const startEditingSelected = () => {
    if (selectedZone?.corners) {
      setPendingCorners(addMidpointsIfNeeded([...selectedZone.corners]));
      setIsEditingCorners(true);
    }
  };

  const cancelDrawing = () => {
    setIsDrawing(false);
    setDrawingType(null);
    if (onDrawComplete) onDrawComplete();
  };

  const cancelEditing = () => {
    setIsEditingCorners(false);
    setPendingCorners(null);
  };

  // Bestem om vi er i en "tegne/redigere" tilstand
  const isInEditMode = isDrawing || isEditingCorners;

  return (
    <>
      {/* Sørg for at de tre lag har korrekt z-index ordrer */}
      <PaneInitializer />

      {/* Bekræftelses-dialog */}
      <ConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirm}
      />
      
      {/* Render områder */}
      {showAreas && areas.map(area => (
        <ZonePolygon
          key={`area-${area.id}`}
          zone={area}
          type="area"
          isSelected={selectedZone?.id === area.id && selectedType === 'area'}
          onSelect={handleZoneSelect}
          userRole={userRole}
          isDrawingMode={isInEditMode}
        />
      ))}

      {/* Render arbejdszoner */}
      {showWorkZones && workZones.map(zone => (
        <ZonePolygon
          key={`work-${zone.id}`}
          zone={zone}
          type="workZone"
          isSelected={selectedZone?.id === zone.id && selectedType === 'workZone'}
          onSelect={handleZoneSelect}
          userRole={userRole}
          isDrawingMode={isInEditMode}
        />
      ))}

      {/* Render rengøringszoner */}
      {showCleaningZones && cleaningZones.map(zone => (
        <ZonePolygon
          key={`clean-${zone.id}`}
          zone={zone}
          type="cleaningZone"
          isSelected={selectedZone?.id === zone.id && selectedType === 'cleaningZone'}
          onSelect={handleZoneSelect}
          userRole={userRole}
          isDrawingMode={isInEditMode}
        />
      ))}

      {/* Hjørne-editor (native Leaflet) */}
      {isEditingCorners && pendingCorners && (
        <CornerEditor
          corners={pendingCorners}
          onChange={handleCornersChange}
          color={ZONE_TYPES[drawingType || selectedType]?.color || '#3b82f6'}
          map={map}
        />
      )}

      {/* Tegne-værktøj */}
      {isDrawing && drawingType && (
        <RectangleDrawer
          zoneType={drawingType}
          onComplete={handleDrawComplete}
          onCancel={cancelDrawing}
        />
      )}

      {/* Zone info panel (kun når ikke redigerer) */}
      {selectedZone && !isEditingCorners && !showCreateDialog && (
        <div ref={stopMapInteraction} className="absolute top-4 right-4 z-[1000] w-80 max-h-[calc(100vh-2rem)] overflow-y-auto overscroll-contain bg-black/90 border border-white/20 rounded-lg shadow-lg p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: ZONE_TYPES[selectedType]?.color }} />
                <span className="text-xs uppercase text-gray-400">{ZONE_TYPES[selectedType]?.label}</span>
              </div>
              <h3 className="font-bold text-lg text-white">
                {editingName ? (
                  <div className="flex gap-1">
                    <input
                      value={tempName}
                      onChange={e => setTempName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && renameZone()}
                      className="bg-white/10 border border-white/30 rounded px-2 py-1 text-sm text-white w-full"
                      autoFocus
                      data-testid="zone-rename-input"
                    />
                    <Button size="sm" onClick={renameZone} className="bg-green-600 hover:bg-green-700 px-2">
                      <Save className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingName(false)} className="text-white px-2">
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <span
                    className="cursor-pointer hover:text-blue-300 transition-colors"
                    onClick={() => { setEditingName(true); setTempName(selectedZone.name); }}
                    title="Klik for at omdøbe"
                  >
                    {selectedZone.name}
                  </span>
                )}
              </h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => { setSelectedZone(null); setSelectedType(null); }}>
              <X className="h-4 w-4 text-white" />
            </Button>
          </div>

          {/* Rengøringszone status og handlinger */}
          {selectedType === 'cleaningZone' && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CLEANING_STATUS[selectedZone.status]?.color || '#22c55e' }} />
                <span className="text-sm text-white">{CLEANING_STATUS[selectedZone.status]?.label || 'Rent'}</span>
              </div>
              
              <div className="space-y-2">
                {selectedZone.status === 'clean' && (
                  <Button onClick={() => handleCleaningAction('request-cleaning')} className="w-full bg-red-600 hover:bg-red-700 text-white">
                    Bestil rengøring
                  </Button>
                )}
                {selectedZone.status === 'pending' && (
                  <Button onClick={() => handleCleaningAction('on-my-way')} className="w-full bg-yellow-600 hover:bg-yellow-700 text-white">
                    Jeg er på vej
                  </Button>
                )}
                {(selectedZone.status === 'pending' || selectedZone.status === 'in_progress') && (
                  <Button onClick={() => handleCleaningAction('complete')} className="w-full bg-green-600 hover:bg-green-700 text-white">
                    Rengøring færdig
                  </Button>
                )}
              </div>
              
              {/* Planlagte aktiveringer */}
              {canCreateCleaningZone && (
                <ScheduledTimesManager 
                  zone={selectedZone} 
                  token={token} 
                  onUpdate={loadAllZones}
                />
              )}
            </div>
          )}

          {/* Områdetilknytning — kun for arbejdszoner og rengøringszoner */}
          {(selectedType === 'workZone' || selectedType === 'cleaningZone') &&
            ((selectedType === 'workZone' && isAdmin) || (selectedType === 'cleaningZone' && canCreateCleaningZone)) && (
            <div className="mb-3 border-t border-white/10 pt-3">
              <label className="text-xs uppercase text-gray-400 block mb-1.5">Tilhører område</label>
              <select
                value={selectedZone.area_id || ''}
                onChange={(e) => changeZoneArea(e.target.value)}
                className="w-full bg-white/10 border border-white/30 rounded px-2 py-1.5 text-sm text-white"
                data-testid="zone-area-select"
              >
                <option value="" className="bg-zinc-900">— Intet område —</option>
                {areas.map(a => (
                  <option key={a.id} value={a.id} className="bg-zinc-900">{a.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Område-detaljer: Beskrivelse + tildelte hold (alle tre zonetyper, admin/heart_leader) */}
          {((selectedType === 'area' && isAdmin) ||
            (selectedType === 'workZone' && isAdmin) ||
            (selectedType === 'cleaningZone' && canCreateCleaningZone)) && (
            <>
              <div className="mb-3 border-t border-white/10 pt-3">
                <label className="text-xs uppercase text-gray-400 block mb-1.5">Beskrivelse</label>
                {editingDescription ? (
                  <div className="space-y-2">
                    <textarea
                      value={tempDescription}
                      onChange={(e) => setTempDescription(e.target.value)}
                      rows={3}
                      className="w-full bg-white/10 border border-white/30 rounded px-2 py-1.5 text-sm text-white resize-none"
                      placeholder="Tilføj en beskrivelse..."
                      data-testid="zone-description-input"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={saveDescription} className="bg-green-600 hover:bg-green-700 flex-1" data-testid="zone-description-save-btn">
                        <Save className="h-3 w-3 mr-1" /> Gem
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingDescription(false)} className="text-white">
                        Annuller
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => { setEditingDescription(true); setTempDescription(selectedZone.description || ''); }}
                    className="text-sm text-gray-300 italic cursor-pointer hover:text-white hover:bg-white/5 rounded px-2 py-1 transition-colors min-h-[2rem]"
                    title="Klik for at redigere"
                    data-testid="zone-description-display"
                  >
                    {selectedZone.description?.trim()
                      ? selectedZone.description
                      : <span className="text-gray-500">Klik for at tilføje beskrivelse</span>}
                  </div>
                )}
              </div>

              {/* TILDELTE HOLD (read-only) — kilden er Stamdata > Teams.
                  Område: teams med dette område i assigned_area_ids.
                  Arbejdszone: teams med denne arbejdszone i assigned_work_zone_ids.
                  Rengøringszone: arver fra parent arbejdszone hvis findes, ellers fra område. */}
              {(() => {
                if (!Array.isArray(teams) || teams.length === 0) return null;

                let assignedTeams = [];
                let inheritedFrom = null;  // 'workzone' | 'area' | null

                if (selectedType === 'area') {
                  assignedTeams = teams.filter(t =>
                    (t.assigned_area_ids || []).includes(selectedZone.id) ||
                    t.assigned_area_id === selectedZone.id
                  );
                } else if (selectedType === 'workZone') {
                  assignedTeams = teams.filter(t =>
                    (t.assigned_work_zone_ids || []).includes(selectedZone.id) ||
                    t.assigned_work_zone_id === selectedZone.id
                  );
                } else if (selectedType === 'cleaningZone') {
                  // 1) Hvis rengøringszonen ligger i en arbejdszone, arv dens hold
                  if (selectedZone.work_zone_id) {
                    assignedTeams = teams.filter(t =>
                      (t.assigned_work_zone_ids || []).includes(selectedZone.work_zone_id) ||
                      t.assigned_work_zone_id === selectedZone.work_zone_id
                    );
                    if (assignedTeams.length > 0) inheritedFrom = 'workzone';
                  }
                  // 2) Ellers (eller hvis ingen hold på arbejdszonen): arv fra område
                  if (assignedTeams.length === 0 && selectedZone.area_id) {
                    assignedTeams = teams.filter(t =>
                      (t.assigned_area_ids || []).includes(selectedZone.area_id) ||
                      t.assigned_area_id === selectedZone.area_id
                    );
                    if (assignedTeams.length > 0) inheritedFrom = 'area';
                  }
                }

                return (
                  <div className="mb-3 border-t border-white/10 pt-3" data-testid="zone-assigned-teams">
                    <label className="text-xs uppercase text-gray-400 block mb-1.5">
                      Tildelte hold
                      {inheritedFrom === 'workzone' && (
                        <span className="ml-1 normal-case text-[10px] text-gray-500">(arvet fra arbejdszone)</span>
                      )}
                      {inheritedFrom === 'area' && (
                        <span className="ml-1 normal-case text-[10px] text-gray-500">(arvet fra område)</span>
                      )}
                    </label>
                    {assignedTeams.length === 0 ? (
                      <p className="text-xs text-gray-500 italic">Ingen hold tildelt. Rediger i Stamdata &gt; Teams.</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {assignedTeams.map(team => (
                          <span
                            key={team.id}
                            className="inline-flex items-center px-2 py-1 rounded bg-indigo-500/20 text-indigo-200 text-xs"
                            data-testid={`zone-assigned-team-${team.id}`}
                          >
                            {team.name}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-[10px] text-gray-500 mt-1.5">Redigeres i Stamdata &gt; Teams</p>
                  </div>
                );
              })()}
            </>
          )}

          {/* Rediger/slet knapper */}
          <div className="space-y-2 border-t border-white/10 pt-3">
            {((selectedType === 'area' && isAdmin) ||
              (selectedType === 'workZone' && isAdmin) ||
              (selectedType === 'cleaningZone' && canCreateCleaningZone)) && (
              <>
                <Button onClick={startEditingSelected} variant="outline" size="sm" className="w-full text-white border-white/30" data-testid="edit-zone-corners-btn">
                  <MapPin className="h-4 w-4 mr-2" />
                  Justér hjørner
                </Button>
                <Button onClick={deleteZone} variant="outline" size="sm" className="w-full text-red-400 border-red-400/30 hover:bg-red-500/10" data-testid="delete-zone-btn">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Slet
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Redigerings-panel (når vi redigerer eksisterende zone) */}
      {isEditingCorners && !showCreateDialog && selectedZone && (
        <div ref={stopMapInteraction} className="absolute top-4 right-4 z-[1000] w-80 max-h-[calc(100vh-2rem)] overflow-y-auto overscroll-contain bg-black/90 border border-white/20 rounded-lg shadow-lg p-4">
          <h3 className="font-bold text-lg text-white mb-2">Justér hjørner</h3>
          <p className="text-sm text-gray-400 mb-4">Træk de nummererede hjørner for at justere formen</p>
          <div className="flex gap-2">
            <Button onClick={updateZoneCorners} className="flex-1 bg-green-600 hover:bg-green-700">
              <Save className="h-4 w-4 mr-2" />
              Gem
            </Button>
            <Button onClick={cancelEditing} variant="outline" className="text-white border-white/30">
              Annuller
            </Button>
          </div>
        </div>
      )}

      {/* Opret zone dialog */}
      {showCreateDialog && (
        <div ref={stopMapInteraction} className="absolute top-4 right-4 z-[1000] w-96 max-h-[calc(100vh-2rem)] overflow-y-auto overscroll-contain bg-black/95 border border-white/20 rounded-lg shadow-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: ZONE_TYPES[drawingType]?.color }} />
            <h3 className="text-lg font-bold text-white">Opret {ZONE_TYPES[drawingType]?.label}</h3>
          </div>
          
          <p className="text-sm text-green-400 mb-4">
            ✓ Træk hjørnerne på kortet for at justere formen
          </p>
          
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-400">Navn</Label>
              <Input
                value={newZoneName}
                onChange={(e) => setNewZoneName(e.target.value)}
                placeholder="F.eks. Meadow eller Scene A front"
                className="mt-1 bg-white/10 border-white/20 text-white"
              />
            </div>

            {(drawingType === 'workZone' || drawingType === 'cleaningZone') && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-400">Tilhører område</Label>
                <select
                  value={selectedAreaId}
                  onChange={(e) => setSelectedAreaId(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-white/20 bg-zinc-800 px-3 py-2 text-sm text-white mt-1"
                >
                  <option value="" className="bg-zinc-800 text-white">Vælg område...</option>
                  {areas.map(area => (
                    <option key={area.id} value={area.id} className="bg-zinc-800 text-white">{area.name}</option>
                  ))}
                </select>
                {areas.length === 0 && <p className="text-xs text-yellow-400 mt-1">Opret først et område</p>}
              </div>
            )}

            {drawingType === 'cleaningZone' && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-400">
                  <Clock className="h-3 w-3 inline mr-1" />
                  Planlagte aktiveringer (valgfrit)
                </Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="datetime-local"
                    value={newScheduledTime}
                    onChange={(e) => setNewScheduledTime(e.target.value)}
                    className="flex-1 bg-white/10 border-white/20 text-white"
                    min={new Date().toISOString().slice(0, 16)}
                    max="2099-12-31T23:59"
                  />
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-white border-white/30 hover:bg-white/10 shrink-0 px-2"
                        aria-label="Vælg dag fra mini-kalender"
                        data-testid="cleaning-zone-create-date-picker-btn"
                      >
                        <CalendarDays className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <MiniCalendar
                        mode="single"
                        locale={da}
                        weekStartsOn={1}
                        selected={newScheduledTime && newScheduledTime.includes('T') ? new Date(newScheduledTime) : undefined}
                        onSelect={(d) => {
                          if (!d) return;
                          const y = d.getFullYear();
                          const m = String(d.getMonth() + 1).padStart(2, '0');
                          const day = String(d.getDate()).padStart(2, '0');
                          const timePart = (newScheduledTime && newScheduledTime.includes('T')) ? newScheduledTime.split('T')[1] : '06:00';
                          setNewScheduledTime(`${y}-${m}-${day}T${timePart}`);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <Button 
                    type="button" 
                    onClick={addScheduledTime} 
                    variant="outline" 
                    size="sm"
                    className="text-white border-white/30 hover:bg-white/10"
                  >
                    Tilføj
                  </Button>
                </div>
                {scheduledTimes.length > 0 && (
                  <div className="mt-2 space-y-1 max-h-40 overflow-y-auto overscroll-contain pr-1">
                    {scheduledTimes.map((time, idx) => {
                      const date = new Date(time);
                      const day = date.getDate().toString().padStart(2, '0');
                      const month = (date.getMonth() + 1).toString().padStart(2, '0');
                      const hours = date.getHours().toString().padStart(2, '0');
                      const minutes = date.getMinutes().toString().padStart(2, '0');
                      return (
                        <div key={idx} className="flex items-center justify-between bg-white/5 rounded px-2 py-1">
                          <span className="text-sm text-white">
                            {day}/{month} kl. {hours}:{minutes}
                          </span>
                          <button 
                            onClick={() => removeScheduledTime(time)}
                            className="text-red-400 hover:text-red-300 text-xs"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">Zonen bliver automatisk rød på disse tidspunkter</p>
              </div>
            )}

            <div className="text-sm text-gray-400">
              {pendingCorners?.length || 0} hjørner defineret
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={createZone} className="flex-1 bg-primary hover:bg-primary/90">
                <Save className="h-4 w-4 mr-2" />
                Opret
              </Button>
              <Button variant="outline" onClick={resetDialog} className="text-white border-white/30">
                Annuller
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export { ZonePolygon, ZONE_TYPES, CLEANING_STATUS };
